import { FlexBox, Icon, SelectionList } from '@dixa/lib-shared-habla';
import { ReactNode } from 'react';

import { KeyboardKeyCombinations } from 'components/keyboard';
import { CommandLauncherAction } from 'utils/commandLauncher';
import { KeyboardKeyCombination } from 'utils/keyboard';

import styles from '../commandLauncher.module.scss';

/** Keybindings map type from useCommands */
export type KeybindingsMap = Record<string, readonly KeyboardKeyCombination[]>;

/** Groups actions by their section property. */
function groupActionsBySection(
  actions: readonly CommandLauncherAction[],
): Map<string, CommandLauncherAction[]> {
  const sections = new Map<string, CommandLauncherAction[]>();

  for (const action of actions) {
    const sectionName = action.section ?? 'Actions';
    const sectionActions = sections.get(sectionName) ?? [];
    sections.set(sectionName, [...sectionActions, action]);
  }

  return sections;
}

/**
 * Gets the selection list items for the standard command launcher actions,
 * grouped by section.
 */
export function getCommandLauncherActionItems(
  actions: readonly CommandLauncherAction[],
  keybindings?: KeybindingsMap,
): JSX.Element[] {
  const sections = groupActionsBySection(actions);

  return Array.from(sections.entries()).map(([sectionName, sectionActions]) => (
    <SelectionList.Section key={sectionName} title={sectionName}>
      {sectionActions.map((action) =>
        getCommandLauncherActionItemOrList(action, keybindings),
      )}
    </SelectionList.Section>
  ));
}

function getCommandLauncherActionItemOrList(
  action: CommandLauncherAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  return action.actions
    ? getCommandLauncherActionList(action, keybindings)
    : getCommandLauncherActionItem(action, keybindings);
}

export function getCommandLauncherActionList(
  action: CommandLauncherAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  return (
    <SelectionList.List
      key={action.id}
      parentItem={getCommandLauncherActionItem(action, keybindings)}
    >
      {action.actions?.map((a) =>
        getCommandLauncherActionItemOrList(a, keybindings),
      )}
    </SelectionList.List>
  );
}

/** Gets the shortcut display for an action */
function getShortcutDisplay(
  action: CommandLauncherAction,
  keybindings?: KeybindingsMap,
): ReactNode {
  if (!action.commandName || !keybindings) return null;

  const actionKeybindings = keybindings[action.commandName];
  if (!actionKeybindings?.length) return null;

  // Use the existing KeyboardKeyCombinations component with simple variant
  return (
    <KeyboardKeyCombinations
      keyCombinations={actionKeybindings}
      variant="simple"
    />
  );
}

export function getCommandLauncherActionItem(
  action: CommandLauncherAction,
  keybindings?: KeybindingsMap,
): JSX.Element {
  const shortcut = getShortcutDisplay(action, keybindings);
  const hasIconOrPrefix = action.icon || action.prefix;

  return (
    <SelectionList.Item
      key={action.id}
      value={action}
      noFixedSelectionStateSpace
      className={styles.item}
    >
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        className={styles.actionItem}
      >
        <FlexBox alignItems="center" gap="300">
          {hasIconOrPrefix && (
            <span className={styles.actionItemIcon}>
              {action.icon && (
                <Icon name={action.icon} size={16} color={action.iconColor} />
              )}
              {action.prefix}
            </span>
          )}
          <span>
            {action.label}
            {action.suffix}
          </span>
        </FlexBox>
        {shortcut}
      </FlexBox>
    </SelectionList.Item>
  );
}
